import React, { Children, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";

import requestIdleCallback from "utilities/requestIdleCallback";
import cancelIdleCallback from "utilities/cancelIdleCallback";

import GridRowsInViewPort from "./GridRowsInViewPortArea";

const GridRowsInViewPortWrapper = ({ children, rowHeight = 40 }) => {
  const arrayChildren = useMemo(() => Children.toArray(children), [children]);

  const focusedIndex = useRef(null);
  const entriesToHide = useRef(new Set());
  const idleCallbackId = useRef(null);
  const rowElementsWeakMap = useRef(new WeakMap());

  const [observer, setObserver] = useState();
  const [rowsDefaultVisiblityState, setRowsDefaultVisiblityState] = useState(false);

  const setFocusedIndex = useCallback(index => {
    focusedIndex.current = index;
  }, []);

  useEffect(() => {
    const handleBeforePrint = () => {
      flushSync(() => {
        setRowsDefaultVisiblityState(true);
      });
    };

    const handleAfterPrint = () => {
      setRowsDefaultVisiblityState(false);
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    const handleDelayedHide = () => {
      cancelIdleCallback(idleCallbackId.current);
      idleCallbackId.current = requestIdleCallback(() => {
        while (entriesToHide.current.size > 0) {
          const target = entriesToHide.current.values().next().value;
          rowElementsWeakMap.current.get(target)(false);
          entriesToHide.current.delete(target);
        }
      });
    };

    // Intersection observer setup
    const intersectionCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entriesToHide.current.delete(entry.target);
          rowElementsWeakMap.current.get(entry.target)(true);
        } else {
          entriesToHide.current.add(entry.target);
        }
      });
      handleDelayedHide();
    };

    var observerRef;
    const handleSheetsObserver = () => {
      const observerOptions = {
        rootMargin: `${window.innerHeight}px 0px ${window.innerHeight}px 0px`
      };
      if (
        "IntersectionObserver" in window &&
        "IntersectionObserverEntry" in window &&
        "intersectionRatio" in window.IntersectionObserverEntry.prototype
      ) {
        observerRef = new IntersectionObserver(intersectionCallback, observerOptions);
      }
    };

    handleSheetsObserver();
    setObserver(observerRef);

    return () => {
      entriesToHide.current.clear();
      observerRef.disconnect();
    };
  }, []);

  const bottomIndexes = children.length > 10 ? children.length - 10 : -1;

  return (
    <>
      {Children.map(arrayChildren, (child, index) =>
        child.props.isWithinVisibleArea ? (
          child
        ) : (
          <GridRowsInViewPort
            isLastSection={child.props.isLastSection}
            index={index}
            observer={observer}
            bottomIndexes={bottomIndexes}
            focusedIndex={focusedIndex}
            rowsDefaultVisiblityState={rowsDefaultVisiblityState}
            setFocusedIndex={setFocusedIndex}
            rowElementsWeakMap={rowElementsWeakMap}
          >
            {child}
          </GridRowsInViewPort>
        )
      )}
    </>
  );
};

export default React.memo(GridRowsInViewPortWrapper);
